import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PLANTS_NAME, CHART_TITLE_PLANTAS } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import BaseHighchart from '../../BaseHighchart';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import {
  ChartDataType,
} from '../../types';
import {
  calculateYAxisLimits,
  getChartConfig,
  getLinearTooltip,
  getYAxis,
} from '../utils';
import calculateDailyMaxPlant from './utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import PlantsConfigContent from './PlantsConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function PlantsChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_PLANTAS,
  yAxisTitle = 'Mediciones de plantas (µm)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  refreshTrigger,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  refreshTrigger?: number;
}) {
  const chartName = 'planta';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const { data } = await apiClient.readings.getPlantData({
          farmId,
          sectorId,
          startDate: interval.startDate,
          endDate: interval.endDate,
          telemetryType: selectedSector?.telemetryType,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector?.sn : undefined,
          chart: PLANTS_NAME,
        });

        const maxSeries = calculateDailyMaxPlant(data.series);
        setChartData({
          series: [...data.series, ...maxSeries],
          absoluteCategories: data.absoluteCategories,
        });
      }
    } catch {
      // fetch error handled silently
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval?.startDate,
    interval?.endDate,
    selectedSector,
    sectorId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(
      this: Highcharts.TooltipFormatterContextObject,
    ) {
      return getLinearTooltip(this, conversations);
    },
  }), [conversations, chartData.absoluteCategories]);

  const chartConfig = useMemo(() => {
    if (!interval) return null;

    const yLimits = calculateYAxisLimits(chartData.series);

    return getChartConfig(
      chartData,
      height,
      hideXValues,
      {
        ...(getYAxis(yAxisTitle, hideYValues) as Highcharts.YAxisOptions),
        min: yLimits.min,
        max: yLimits.max,
      },
      dayjs(interval.startDate).valueOf(),
      dayjs(interval.endDate).valueOf(),
    );
  }, [
    hideXValues,
    chartData.absoluteCategories,
    hideYValues,
    yAxisTitle,
    height,
    interval,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            PLANTS_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations, enqueueSnackbar]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  return (
    <>
      <Box>
        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            hideYValues={hideYValues}
            adjustYWithLegend={true}
            showFullScreen={true}
            legend={{ enabled: !hideLegend }}
            chartConfig={chartConfig}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            absoluteCategories={chartData.absoluteCategories}
            setModalState={setModalState}
            conversations={conversations}
            height={height}
            zoomSync={true}
            autoAdjustYAxis={true}
            interval={interval}
            graphName={PLANTS_NAME}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={PLANTS_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Plantas"
      >
        <PlantsConfigContent
          sectorId={sectorId}
          onConfigSaved={() => fetchData()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default PlantsChart;
